import { Component, createEffect, createMemo, createSignal, on, Show } from 'solid-js'
import { A, useLocation, useNavigate, useSearchParams } from '@solidjs/router'
import Alert from '../../shared/Alert'
import Divider from '../../shared/Divider'
import { ACCOUNT_KEY, settingStore, toastStore, userStore } from '../../store'
import { getStrictForm, setComponentPageTitle, storage } from '../../shared/util'
import TextInput from '../../shared/TextInput'
import Button from '../../shared/Button'
import { isLoggedIn } from '/web/store/api'
import { TitleCard } from '/web/shared/Card'
import LoginFormModal from './LoginFormModal'
import { Page } from '/web/Layout'
import { useGoogleReady } from '/web/shared/hooks'
import PageHeader from '/web/shared/PageHeader'
import RegisterFormModal from './RegisterFormModal'

const LoginPage: Component = () => {
  const store = userStore()
  const cfg = settingStore()

  const [register, setRegister] = createSignal(false)

  /** Friendly error message passed out of the mutation, if it exists. */
  const loginError = createMemo(() => {
    if (!store.error) return null
    if (store.error.includes('NetworkError')) {
      return "We couldn't reach our servers."
    }

    return 'Something went wrong.'
  })

  return (
    <Page class="flex w-full flex-col items-center">
      <div class="w-full max-w-sm">
        <Show when={register()}>
          <RegisterForm isLoading={store.loading} onClickLogin={() => setRegister(false)} />
        </Show>
        <Show when={!register()}>
          <LoginForm isLoading={store.loading} onClickRegister={() => setRegister(true)} />
        </Show>
        <Show when={loginError()}>
          <Divider />
          <Alert schema="error" title="Failed to log in.">
            {loginError()}
          </Alert>
        </Show>
      </div>
    </Page>
  )
}

export default LoginPage

type FormProps = { isLoading: boolean; onClickRegister?: () => void; onClickLogin?: () => void }

const RegisterForm: Component<FormProps> = (props) => {
  const navigate = useNavigate()
  const register = (evt: Event) => {
    const { username, password, email } = getStrictForm(evt, {
      // handle: 'string',
      username: 'string',
      email: 'string',
      password: 'string',
      // confirm: 'string',
    })

    let userHandle = username

    if (!username || !password || !email) return

    // if (password !== confirm) {
    //   toastStore.warn('Passwords do not match', 2)
    //   return
    // }
    try {
      userStore.register({ handle: userHandle, username, password, email }, () => {
        userStore.loginModal(false)
        window.location.reload()
      })
    } catch (error: any) {
      console.log(error)
      toastStore.warn(error.message, 2)
    }
  }

  return <RegisterFormModal onClickLogin={props.onClickLogin} onClickRegister={register} />

  return (
    <form onSubmit={register} class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <TextInput label="Display Name" fieldName="handle" placeholder="Display name" required />
        <TextInput label="Username" fieldName="username" placeholder="Username" required />
        <TextInput
          label="Password"
          fieldName="password"
          placeholder="Password"
          type="password"
          required
        />
        <TextInput fieldName="confirm" placeholder="Confirm Password" type="password" required />
      </div>

      <Button type="submit" disabled={props.isLoading}>
        {props.isLoading ? 'Registering...' : 'Register'}
      </Button>
    </form>
  )
}

const LoginForm: Component<FormProps> = (props) => {
  let refGoogle: any
  const navigate = useNavigate()
  const [query] = useSearchParams()
  const loc = useLocation()
  const state = settingStore()
  const [error, setError] = createSignal<string>()
  const google = useGoogleReady()

  createEffect(() => {
    if (state.initLoading) return

    if (query.callback && isLoggedIn()) {
      for (const authUrl of state.config.authUrls) {
        if (query.callback.startsWith(authUrl)) return handleLogin()
      }
      setError('Invalid callback URL')
      return
    }
  })

  createEffect(
    on(
      () => google(),
      () => {
        const win: any = window
        const api = win.google?.accounts?.id

        if (!api) return

        if (state.config.serverConfig?.googleClientId) {
          api.initialize({
            client_id: state.config.serverConfig?.googleClientId,
            callback: (result: any) => {
              userStore.handleGoogleCallback('login', result, () => navigate('/dashboard'))
            },
          })

          api.renderButton(refGoogle, {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            text: 'signin_with',
          })
        }
      }
    )
  )

  const handleLogin = () => {
    userStore.remoteLogin((token) => {
      location.href = `${query.callback}?access_token=${token}`
    })
  }

  const login = (evt: Event) => {
    const { username, password } = getStrictForm(evt, { username: 'string', password: 'string' })
    if (!username || !password) return

    userStore.login(username, password, () => {
      if (query.callback) {
        handleLogin()
        return
      }

      userStore.loginModal(false)

      // navigate('/dashboard')
      window.location.reload()
    })
  }

  return <LoginFormModal onClickLogin={login} onClickRegister={props.onClickRegister} />

  return (
    <form onSubmit={login} class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <label class="font-clash font-semibold" for="username">
          Username or Email
        </label>
        <TextInput
          fieldName="username"
          placeholder="Enter Username or Email"
          required
          value={loc.pathname.includes('/remember') ? storage.localGetItem(ACCOUNT_KEY) || '' : ''}
        />
        <label for="password">Password</label>
        <TextInput fieldName="password" placeholder="Enter Password" type="password" required />
      </div>

      <Show when={error()}>
        <TitleCard type="rose">{error()}</TitleCard>
      </Show>

      <Button type="submit" disabled={props.isLoading || !!error()}>
        {props.isLoading ? 'Logging in...' : 'Login'}
      </Button>

      <div
        class="flex justify-center"
        ref={(ref) => {
          refGoogle = ref
        }}
        id="g_id_onload"
        data-context="signin"
        data-ux_mode="popup"
        data-login_uri={`${location.origin}/oauth/google`}
        data-itp_support="true"
      ></div>
    </form>
  )
}
